"use client";

import { createContext, useContext, type ReactNode } from "react";
import {
    DEFAULT_NAV_THEME,
    navThemeStyles,
    type NavTheme,
    type NavThemeStyles,
} from "./nav-theme";

type NavThemeContextValue = {
    theme: NavTheme;
    mobileTheme: NavTheme;
};

const NavThemeContext = createContext<NavThemeContextValue>({
    theme: DEFAULT_NAV_THEME,
    mobileTheme: DEFAULT_NAV_THEME,
});

type NavThemeProviderProps = {
    value: NavTheme;
    /** When set, mobile header/menu use this theme (desktop keeps `value`). */
    mobileTheme?: NavTheme;
    children: ReactNode;
};

/** Must be a function component — re-exporting Context.Provider breaks RSC/lazy. */
export function NavThemeProvider({
    value,
    mobileTheme,
    children,
}: NavThemeProviderProps) {
    return (
        <NavThemeContext.Provider
            value={{ theme: value, mobileTheme: mobileTheme ?? value }}
        >
            {children}
        </NavThemeContext.Provider>
    );
}

export function useNavTheme(): NavTheme {
    return useContext(NavThemeContext).theme;
}

export function useNavMobileTheme(): NavTheme {
    return useContext(NavThemeContext).mobileTheme;
}

export function useNavThemeStyles(): NavThemeStyles {
    return navThemeStyles[useNavTheme()];
}

export function useNavMobileThemeStyles(): NavThemeStyles {
    return navThemeStyles[useNavMobileTheme()];
}

export type { NavTheme, NavThemeStyles };
export { DEFAULT_NAV_THEME, navThemeStyles };
